import React, { useState } from 'react';
import { Camera, Image, Video, Mountain, User, Euro, Building, Dog, Newspaper, Star, AppWindow } from 'lucide-react';
import Papa from 'papaparse';
import '../assets/quiz-style.css';

const questions = [
  {
    id: 'budget',
    text: 'Welches Budget hast du zur Verfügung?',
    options: [
      { value: 'entry', label: '0 - 1.000€', icon: Euro },
      { value: 'mid', label: '1.001 - 2.000€', icon: Euro },
      { value: 'high', label: '2.001 - 5.000€', icon: Euro },
      { value: 'professional', label: 'Über 5.000€', icon: Euro }
    ],
    multiSelect: false
  },
  {
    id: 'experience',
    text: 'Wie viel Erfahrung hast du mit Fotografie?',
    options: [
      { value: 'beginner', label: 'Absoluter Anfänger', icon: Camera },
      { value: 'hobby', label: 'Hobby-Fotograf', icon: Image },
      { value: 'advanced', label: 'Fortgeschritten', icon: Video },
      { value: 'professional', label: 'Profi', icon: Camera, showOnlyForBudget: ['high', 'professional'] }
    ],
    multiSelect: false
  },
  {
    id: 'primaryUse',
    text: 'Für welche Bereiche möchtest du die Kamera hauptsächlich nutzen? (Wähle bis zu 3 aus)',
    options: [
      { value: 'landscape', label: 'Natur- und Landschaftsfotografie', icon: Mountain },
      { value: 'portrait', label: 'Portraitfotografie', icon: User },
      { value: 'studio', label: 'Studiofotografie', icon: Building },
      { value: 'wildlife', label: 'Tierfotografie', icon: Dog },
      { value: 'reportage', label: 'Reportagefotografie', icon: Newspaper },
      { value: 'astro', label: 'Astrofotografie', icon: Star },
      { value: 'allround', label: 'Ich interessiere mich für alles', icon: AppWindow }
    ],
    multiSelect: true,
    maxSelections: 3,
    allowContinue: true
  },
  {
    id: 'size',
    text: 'Wie wichtig ist dir die Größe der Kamera?',
    options: [
      { value: 'very_compact', label: 'Möglichst kompakt für Reisen', icon: Camera },
      { value: 'balanced', label: 'Guter Kompromiss aus Größe und Handling', icon: Image },
      { value: 'professional', label: 'Professionelles Handling wichtiger als Größe', icon: Camera }
    ],
    multiSelect: false
  },
  {
    id: 'video',
    text: 'Wie wichtig sind dir Videofunktionen?',
    options: [
      { value: 'not_important', label: 'Gar nicht wichtig', icon: Image },
      { value: 'nice_to_have', label: 'Wäre praktisch, aber nicht entscheidend', icon: Video },
      { value: 'important', label: 'Sehr wichtig, plane regelmäßig Videos', icon: Video },
      { value: 'professional', label: 'Professionelle Videonutzung geplant', icon: Video }
    ],
    multiSelect: false
  }
];

// Erwartet in der CSV pro Kamera folgende Zahlen-Spalten (Werte 0-5, keine Lücken):
// "Natur- und Landschaftsfotografie", "Portraitfotografie", "Studiofotografie",
// "Tierfotografie", "Reportagefotografie", "Astrofotografie", "Videofunktion"
// Außerdem Textspalten "Vorteile" und "Herstellerhinweise" für die Anzeige.
const calculateCameraScore = (camera, preferences) => {
    let totalScore = 0;
    let maxPossibleScore = 0;

    // Einsatzgebiete Score
    const useMapping = {
        'landscape': 'Natur- und Landschaftsfotografie',
        'portrait': 'Portraitfotografie',
        'studio': 'Studiofotografie',
        'wildlife': 'Tierfotografie',
        'reportage': 'Reportagefotografie',
        'astro': 'Astrofotografie'
    };

    // Wenn "Allrounder" gewählt wurde, alle Bereiche gleichmäßig gewichten
    // Wichtig: camera[field] ?? 0 statt camera[field] -> fehlende/leere Werte
    // werden als 0 gewertet statt den ganzen Score zu NaN zu machen.
    if (preferences.primaryUse.includes('allround')) {
        Object.values(useMapping).forEach(field => {
            totalScore += camera[field] ?? 0;
            maxPossibleScore += 7; // Maximale Bewertung pro Bereich
        });
    } else {
        // Sonst nur gewählte Bereiche bewerten.
        // Wichtig: maxPossibleScore wird für JEDE gewählte Kategorie erhöht,
        // unabhängig vom Wert der Kamera in dieser Kategorie. Vorher wurde
        // eine Kategorie mit Wert 0 komplett übersprungen (0 ist falsy),
        // wodurch der Nenner zwischen Kameras unterschiedlich hoch war und
        // die Prozentwerte nicht mehr vergleichbar waren.
        preferences.primaryUse.forEach(use => {
            const field = useMapping[use];
            if (field) {
                totalScore += camera[field] ?? 0;
                maxPossibleScore += 7;
            }
        });
    }

    // Video-Score: datenbasiert über die Spalte "Videofunktion" (0-5),
    // statt vorher über eine Marken-Namens-Erkennung im Modellnamen.
    // Dadurch werden auch videostarke Modelle abseits von Sony/Canon/
    // Panasonic/Lumix (z.B. Nikon Z8, Fujifilm X-H2S) korrekt bewertet.
    if (preferences.video === 'important' || preferences.video === 'professional') {
        totalScore += camera['Videofunktion'] ?? 0;
        maxPossibleScore += 7;
    }

    // Größen-Kompatibilität
    const sensorSizes = {
        'Micro Four Thirds': 1,
        'APS-C': 2,
        'Vollformat': 3,
        'Mittelformat': 4
    };

    let sizeScore = 0;
    switch(preferences.size) {
        case 'very_compact':
            sizeScore = sensorSizes[camera.Sensorgröße] === 1 ? 10 : 
                       sensorSizes[camera.Sensorgröße] === 2 ? 5 : 0;
            break;
        case 'balanced':
            sizeScore = sensorSizes[camera.Sensorgröße] <= 2 ? 10 : 5;
            break;
        case 'professional':
            sizeScore = sensorSizes[camera.Sensorgröße] >= 2 ? 10 : 5;
            break;
    }
    totalScore += sizeScore;
    maxPossibleScore += 10;

    // Normalisiere Score auf 100
    return (totalScore / maxPossibleScore) * 100;
};

export default function CameraQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCameraData = async () => {
    try {
      const response = await fetch(cameraQuizAjax.ajaxurl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'get_camera_csv',
          nonce: cameraQuizAjax.nonce
        })
      });

      const result = await response.json();
      if (result.success) {
        return Papa.parse(result.data, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        }).data;
      }
      throw new Error('Fehler beim Laden der Kameradaten');
    } catch (error) {
      console.error('Error loading camera data:', error);
      return null;
    }
  };

  const findCameraRecommendation = async () => {
    const parsedData = await loadCameraData();
    
    if (!parsedData || parsedData.length === 0) {
        return {
            main: null,
            alternatives: []
        };
    }

    const budgetMapping = {
      'entry': '0-1000€',
      'mid': '1001-2000€',
      'high': '2001-5000€',
      'professional': 'ab 5000€'
    };

    // Filtere zuerst nach Budget und Erfahrungslevel
    let matchingCameras = parsedData.filter(camera => {
      // Budget-Filter
      if (camera.Preissegment !== budgetMapping[answers.budget]) return false;
      
      // Experience-Level-Filter
      if (camera.Stufe !== answers.experience) return false;

      return true;
    });

    // Wenn keine Kameras gefunden wurden, verwende alle Kameras aus dem Preissegment
    if (matchingCameras.length === 0) {
        matchingCameras = parsedData.filter(camera => 
            camera.Preissegment === budgetMapping[answers.budget]
        );
    }

    // Berechne Scores für alle passenden Kameras
    const scoredCameras = matchingCameras.map(camera => ({
      ...camera,
      score: calculateCameraScore(camera, {
        primaryUse: Array.isArray(answers.primaryUse) ? answers.primaryUse : [answers.primaryUse],
        size: answers.size,
        video: answers.video
      })
    }));

    // Sortiere nach Score
    scoredCameras.sort((a, b) => b.score - a.score);

    // Wähle immer die beste Kamera und bis zu zwei Alternativen
    const mainCamera = scoredCameras[0];
    const alternatives = scoredCameras.slice(1, 3);

    return {
      main: {
        model: mainCamera.Modell,
        description: mainCamera.Vorteile,
        price: mainCamera.Preissegment,
        score: Math.round(mainCamera.score),
        highlights: [
          `Sensorgröße: ${mainCamera.Sensorgröße}`,
          `Einsatzgebiete-Score: ${Math.round(mainCamera.score)}%`,
          `Herstellerhinweise: ${mainCamera.Herstellerhinweise}`
        ].filter(Boolean)
      },
      alternatives: alternatives.map(camera => ({
        model: camera.Modell,
        description: camera.Vorteile,
        price: camera.Preissegment,
        score: Math.round(camera.score),
        highlights: [
          `Sensorgröße: ${camera.Sensorgröße}`,
          `Einsatzgebiete-Score: ${Math.round(camera.score)}%`
        ].filter(Boolean)
      }))
    };
  };

  const handleAnswer = (questionId, value) => {
    const currentQ = questions[currentQuestion];
    
    if (currentQ.multiSelect) {
      // Wenn "Allrounder" gewählt wird, setze nur diese Option
      if (value === 'allround') {
        setAnswers(prev => ({
          ...prev,
          [questionId]: ['allround']
        }));
        moveToNextQuestion();
        return;
      }

      const currentSelections = answers[questionId] || [];
      let newSelections;

      if (currentSelections.includes(value)) {
        // Option abwählen
        newSelections = currentSelections.filter(v => v !== value);
      } else {
        // Option hinzufügen
        if (currentSelections.length < currentQ.maxSelections) {
          newSelections = [...currentSelections, value];
        } else {
          return; // Maximum erreicht
        }
      }

      setAnswers(prev => ({
        ...prev,
        [questionId]: newSelections
      }));
    } else {
      // Einzelauswahl
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
      moveToNextQuestion();
    }
  };

  const handleContinue = () => {
    const currentSelections = getCurrentSelections();
    if (currentSelections.length > 0) {
      moveToNextQuestion();
    }
  };

  const moveToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setLoading(true);
    try {
      const result = await findCameraRecommendation();
      setRecommendation(result);
      setShowResult(true);
    } catch (error) {
      console.error('Error finding camera recommendation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setRecommendation(null);
  };

  const getCurrentSelections = () => {
    const currentQ = questions[currentQuestion];
    return answers[currentQ.id] || (currentQ.multiSelect ? [] : '');
  };

  const getRemainingSelections = () => {
    const currentQ = questions[currentQuestion];
    if (currentQ.multiSelect) {
      const currentSelections = getCurrentSelections();
      return currentQ.maxSelections - currentSelections.length;
    }
    return 0;
  };

  if (showResult && recommendation && recommendation.main) {
    return (
      <div className="camera-quiz-container">
        <div className="quiz-card">
          <div className="quiz-content">
            <h2 className="quiz-title">Deine perfekte Kamera</h2>
            <div className="quiz-results-container">
              <div className="quiz-result">
                <h3 className="quiz-result-title">{recommendation.main.model}</h3>
                <p className="quiz-result-description">{recommendation.main.description}</p>
                <p className="quiz-score">Übereinstimmung mit deinen Anforderungen: {recommendation.main.score}%</p>
                <p className="quiz-result-price">{recommendation.main.price}</p>
                <div className="quiz-result-highlights">
                  <h4 className="quiz-highlights-title">Highlights:</h4>
                  <ul className="quiz-highlights-list">
                    {recommendation.main.highlights.map((highlight, index) => (
                      <li key={index} className="quiz-highlight-item">
                        <span className="quiz-bullet">•</span>
                        <span className="quiz-highlight-text">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {recommendation.alternatives.length > 0 && (
                <div className="quiz-alternatives">
                  <h3 className="quiz-alternatives-title">Alternative Empfehlungen:</h3>
                  {recommendation.alternatives.map((alt, index) => (
                    <div key={index} className="quiz-alternative">
                      <h4 className="quiz-alternative-title">{alt.model}</h4>
                      <p className="quiz-alternative-description">{alt.description}</p>
                      <p className="quiz-score">Übereinstimmung mit deinen Anforderungen: {alt.score}%</p>
                      <p className="quiz-alternative-price">{alt.price}</p>
                      <ul className="quiz-highlights-list">
                        {alt.highlights.map((highlight, idx) => (
                          <li key={idx} className="quiz-highlight-item">
                            <span className="quiz-bullet">•</span>
                            <span className="quiz-highlight-text">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              
              <button 
                onClick={resetQuiz}
                className="quiz-restart-button"
              >
                Quiz neu starten
              </button>
            </div>
            {loading && (
              <div className="quiz-loading">
                <p>Suche passende Kamera...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const currentSelections = getCurrentSelections();
  const remainingSelections = getRemainingSelections();

  return (
    <div className="camera-quiz-container">
      <div className="quiz-card">
        <div className="quiz-content">
          <div className="quiz-header">
            <div className="quiz-progress">
              {questions.map((_, index) => (
                <div
                  key={index}
                  className={`quiz-progress-step ${
                    index <= currentQuestion ? 'active' : ''
                  }`}
                />
              ))}
            </div>
            <h2 className="quiz-question">{currentQ.text}</h2>
            {currentQ.multiSelect && remainingSelections > 0 && (
              <p className="quiz-selection-counter">
                Noch {remainingSelections} {remainingSelections === 1 ? 'Auswahl' : 'Auswahlen'} möglich
              </p>
            )}
          </div>
          <div className="quiz-options">
            {currentQ.options
              .filter(option => {
                if (option.showOnlyForBudget) {
                  return option.showOnlyForBudget.includes(answers.budget);
                }
                return true;
              })
              .map((option) => {
                const isSelected = Array.isArray(currentSelections) 
                  ? currentSelections.includes(option.value)
                  : currentSelections === option.value;
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQ.id, option.value)}
                    className={`quiz-button ${isSelected ? 'selected' : ''}`}
                    disabled={loading || (currentQ.multiSelect && !isSelected && remainingSelections === 0)}
                  >
                    <Icon className="quiz-button-icon" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
          </div>
          <div className="quiz-navigation">
            {currentQuestion > 0 && (
              <button
                onClick={handleBack}
                className="quiz-back-button"
              >
                Zurück zur vorherigen Frage
              </button>
            )}
            {currentQ.allowContinue && currentSelections.length > 0 && (
              <button
                onClick={handleContinue}
                className="quiz-continue-button"
              >
                Weiter
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}